import { Component } from '@theme/component';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

/**
 * Plays only the visible breakpoint video so the hidden file is not downloaded.
 *
 * @extends {Component}
 */
export class ResponsiveVideo extends Component {
  /** @type {MediaQueryList} */
  #reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    for (const video of this.#videos()) {
      video.addEventListener('playing', this.#onPlaying);
    }

    this.#sync();
    mediaQueryLarge.addEventListener('change', this.#sync);
    this.#reducedMotionQuery.addEventListener('change', this.#sync);
  }

  disconnectedCallback() {
    for (const video of this.#videos()) {
      video.removeEventListener('playing', this.#onPlaying);
      this.#deactivate(video);
    }

    mediaQueryLarge.removeEventListener('change', this.#sync);
    this.#reducedMotionQuery.removeEventListener('change', this.#sync);
    super.disconnectedCallback();
  }

  /** @returns {HTMLVideoElement[]} */
  #videos() {
    return [...this.querySelectorAll('video')];
  }

  /**
   * @param {Event} event
   */
  #onPlaying = (event) => {
    if (event.target instanceof HTMLVideoElement) {
      event.target.classList.add('is-playing');
    }
  };

  #sync = () => {
    const desktop = this.querySelector('.slide__media--desktop');
    const mobile = this.querySelector('.slide__media--mobile');
    const active = mediaQueryLarge.matches ? desktop : mobile;
    const inactive = mediaQueryLarge.matches ? mobile : desktop;

    if (inactive instanceof HTMLVideoElement) this.#deactivate(inactive);
    if (prefersReducedMotion()) return;
    if (active instanceof HTMLVideoElement) this.#activate(active);
  };

  /**
   * @param {HTMLVideoElement} video
   */
  #activate(video) {
    let needsLoad = false;

    for (const source of video.querySelectorAll('source[data-video-source]')) {
      const url = source instanceof HTMLSourceElement ? source.dataset.videoSource : undefined;
      if (url && source.getAttribute('src') !== url) {
        source.setAttribute('src', url);
        needsLoad = true;
      }
    }

    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    if (needsLoad) video.load();

    const playPromise = video.play();
    if (playPromise) playPromise.catch(() => {});
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #deactivate(video) {
    video.pause();
    video.autoplay = false;
    video.classList.remove('is-playing');

    for (const source of video.querySelectorAll('source')) {
      source.removeAttribute('src');
    }

    video.removeAttribute('src');
    video.load();
  }
}

if (!customElements.get('responsive-video')) {
  customElements.define('responsive-video', ResponsiveVideo);
}
